import { useEffect, useState, useMemo } from 'react';
import { debounce } from 'lodash';
import { useQuery } from '@tanstack/react-query';
import { ChainId, NATIVE_SUI, WRAPPED_SOL } from '@shared/utils/config/networks';
import { WrappedNativeTokens } from '@shared/types/quoting-engine/wrappedNativeTokens';
import { getBasicTokenInfo } from '../pricing/useBasicTokenInfo';
import { findBestMatch } from '@/lib/explore/findBestMatch';
import { getNetworkLogo } from '@/lib/misc/tokenImageHelper';
export const nativeTokens = [{
  tokenName: 'Ethereum',
  token: 'ETH',
  tokenId: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2:1',
  tokenAddress: '0x0000000000000000000000000000000000000000',
  network: 1,
  wrappedAddress: WrappedNativeTokens.get(1)
}, {
  tokenName: 'Avalanche',
  token: 'AVAX',
  tokenId: '0xb31f66aa3c1e785363f0875a1b74e27b85fd66c7:43114',
  tokenAddress: '0x0000000000000000000000000000000000000000',
  network: 43114,
  wrappedAddress: WrappedNativeTokens.get(43114)
}, {
  tokenName: 'Binance',
  token: 'BNB',
  tokenId: '0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c:56',
  tokenAddress: '0x0000000000000000000000000000000000000000',
  network: 56,
  wrappedAddress: WrappedNativeTokens.get(56)
}, {
  tokenName: 'Polygon',
  token: 'POL',
  tokenId: '0x0d500b1d8e8ef31e21c99d1db9a6444d3adf1270:137',
  tokenAddress: '0x0000000000000000000000000000000000000000',
  network: 137,
  wrappedAddress: '0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270'
}, {
  tokenName: 'Sui',
  token: 'SUI',
  tokenId: `${NATIVE_SUI}:${ChainId.SUI}`,
  tokenAddress: NATIVE_SUI,
  network: ChainId.SUI,
  wrappedAddress: NATIVE_SUI
}, {
  tokenName: 'Solana',
  token: 'SOL',
  tokenAddress: WRAPPED_SOL,
  network: 1399811149,
  wrappedAddress: WRAPPED_SOL
}, {
  tokenName: 'Ethereum',
  token: 'ETH',
  tokenAddress: '0x0000000000000000000000000000000000000000',
  network: 8453,
  wrappedAddress: '0x4200000000000000000000000000000000000006'
}, {
  tokenName: 'Ethereum',
  token: 'ETH',
  tokenAddress: '0x0000000000000000000000000000000000000000',
  network: 42161,
  wrappedAddress: '0x82aF49447D8a07e3bd95BD0d56f35241523fBab1'
}, {
  tokenName: 'Ethereum',
  token: 'ETH',
  tokenAddress: '0x0000000000000000000000000000000000000000',
  network: 10,
  wrappedAddress: '0x4200000000000000000000000000000000000006'
}];
export const fetchBatchTokens = async tokens => {
  var _a, _b;
  const tokenIds = tokens.join(',');
  const url = `/api/explore/tokenBatchSearch?tokens=${tokenIds}&limit=${tokens.length}`;
  let result = [];
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const payload = await response.json();
    const data = payload === null || payload === void 0 ? void 0 : payload.data;
    result = (_b = (_a = data.data) === null || _a === void 0 ? void 0 : _a.filterTokens) === null || _b === void 0 ? void 0 : _b.results;
  } catch (error) {
    console.error('searchResults error', error);
  }
  return result || [];
};
export async function fetchTokens({
  limit,
  phrase,
  network,
  txnCountMin = 10,
  liquidityMin = 1000,
  volumeMin = 1000,
  mktCapMin = 5000,
  timeFrame = '24',
  rankingBy = 'marketCap',
  rankingDirection = 'desc',
  addNatives = true,
  sortSearchResultsViaAlgo = true,
  identifier,
  minAgeMinutes = 0,
  filterStables,
  minUniqueTransactions = 25,
  maxAgeMinutes = 999999999999999,
  showPotentialScam = false
}) {
  var _a, _b, _c, _d, _e, _f, _g, _h;
  if (identifier === 'search' && (!phrase || phrase.length < 2)) {
    return [];
  }
  const networks = network.join(',');
  const url = `/api/explore/tokenSearch?limit=${limit}&phrase=${phrase}&network=${networks}&txnCountMin=${txnCountMin}&liquidityMin=${liquidityMin}&volumeMin=${volumeMin}&mktCapMin=${mktCapMin}&timeFrame=${timeFrame}&rankingBy=${rankingBy}&rankingDirection=${rankingDirection}&minAgeMinutes=${minAgeMinutes}&minUniqueTransactions=${minUniqueTransactions}&maxAgeMinutes=${maxAgeMinutes}&showPotentialScam=${showPotentialScam}`;
  let result = [];
  try {
    if (phrase !== '' && (phrase === null || phrase === void 0 ? void 0 : phrase.length) >= 3) {
      const nativeTokenIds = [];
      for (let index = 0; index < nativeTokens.length; index++) {
        if (((_c = (_b = (_a = nativeTokens === null || nativeTokens === void 0 ? void 0 : nativeTokens[index]) === null || _a === void 0 ? void 0 : _a.tokenName) === null || _b === void 0 ? void 0 : _b.toUpperCase()) === null || _c === void 0 ? void 0 : _c.includes(phrase.toUpperCase())) || ((_f = (_e = (_d = nativeTokens === null || nativeTokens === void 0 ? void 0 : nativeTokens[index]) === null || _d === void 0 ? void 0 : _d.token) === null || _e === void 0 ? void 0 : _e.toUpperCase()) === null || _f === void 0 ? void 0 : _f.includes(phrase.toUpperCase()))) {
          nativeTokenIds.push(nativeTokens[index]);
        }
      }
      if (nativeTokenIds.length > 0 && addNatives) {
        await Promise.all((nativeTokenIds || []).map(async tokenInfo => {
          var _a;
          const res = await getBasicTokenInfo(tokenInfo === null || tokenInfo === void 0 ? void 0 : tokenInfo.network, tokenInfo === null || tokenInfo === void 0 ? void 0 : tokenInfo.wrappedAddress);
          const nativeToken = Object.assign({
            token: Object.assign(Object.assign({}, res === null || res === void 0 ? void 0 : res.token), {
              name: tokenInfo === null || tokenInfo === void 0 ? void 0 : tokenInfo.tokenName,
              symbol: tokenInfo === null || tokenInfo === void 0 ? void 0 : tokenInfo.token,
              address: tokenInfo === null || tokenInfo === void 0 ? void 0 : tokenInfo.tokenAddress,
              imageLargeUrl: getNetworkLogo(tokenInfo === null || tokenInfo === void 0 ? void 0 : tokenInfo.network),
              imageSmallUrl: getNetworkLogo(tokenInfo === null || tokenInfo === void 0 ? void 0 : tokenInfo.network),
              imageThumbUrl: getNetworkLogo(tokenInfo === null || tokenInfo === void 0 ? void 0 : tokenInfo.network),
              info: {
                imageLargeUrl: getNetworkLogo(tokenInfo === null || tokenInfo === void 0 ? void 0 : tokenInfo.network),
                imageSmallUrl: getNetworkLogo(tokenInfo === null || tokenInfo === void 0 ? void 0 : tokenInfo.network),
                imageThumbUrl: getNetworkLogo(tokenInfo === null || tokenInfo === void 0 ? void 0 : tokenInfo.network)
              }
            })
          }, (_a = res.tokenInfo) === null || _a === void 0 ? void 0 : _a.results[0]);
          result.push(nativeToken);
        }));
      }
    }
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const payload = await response.json();
    const data = payload === null || payload === void 0 ? void 0 : payload.data;
    result = result.concat((_h = (_g = data.data) === null || _g === void 0 ? void 0 : _g.filterTokens) === null || _h === void 0 ? void 0 : _h.results);
    if (sortSearchResultsViaAlgo) {
      result = findBestMatch(phrase, result);
    }
    const ethTokens = result.filter(result => {
      var _a;
      return (result === null || result === void 0 ? void 0 : result.token) && ((_a = result === null || result === void 0 ? void 0 : result.token) === null || _a === void 0 ? void 0 : _a.networkId) === ChainId.ETHEREUM;
    });
    const bnbTokens = result.filter(result => {
      var _a;
      return (result === null || result === void 0 ? void 0 : result.token) && ((_a = result === null || result === void 0 ? void 0 : result.token) === null || _a === void 0 ? void 0 : _a.networkId) == ChainId.BSC;
    });
    const otherTokens = result === null || result === void 0 ? void 0 : result.filter(result => {
      var _a, _b;
      return !(result === null || result === void 0 ? void 0 : result.token) || ((_a = result === null || result === void 0 ? void 0 : result.token) === null || _a === void 0 ? void 0 : _a.networkId) !== ChainId.ETHEREUM && ((_b = result === null || result === void 0 ? void 0 : result.token) === null || _b === void 0 ? void 0 : _b.networkId) !== ChainId.BSC;
    });
    result = [...otherTokens, ...bnbTokens, ...ethTokens];
    if (filterStables) {
      result = result.filter(result => {
        const usdPrice = parseFloat(result.priceUSD);
        const low12 = parseFloat(result.low12);
        const high12 = parseFloat(result.high12);
        const inThreshold = price => price > 0.99 && price < 1.01;
        if (inThreshold(usdPrice) && inThreshold(low12) && inThreshold(high12)) {
          return false;
        }
        return true;
      });
    }
  } catch (error) {
    console.error('searchResults error', error);
  }
  return result || [];
}
export function useTokenSearch({
  limit,
  phrase,
  network = [1, 137, 10, 56, 43114, 42161, 250, 8453],
  txnCountMin = 1000,
  liquidityMin = 1000,
  volumeMin = 10000,
  mktCapMin = 10000,
  minUniqueTransactions = 25,
  timeFrame = '24',
  rankingBy = 'volume24',
  rankingDirection = 'DESC',
  addNatives = true,
  sortSearchResultsViaAlgo = true,
  minAgeMinutes = -1,
  maxAgeMinutes = 99999999999999,
  identifier,
  filterStables,
  cacheTime = 1000 * 60 * 5,
  showPotentialScam = false,
  enabled = true
}) {
  const [debouncedPhrase, setDebouncedPhrase] = useState(phrase);
  const debouncedSetPhrase = useMemo(() => debounce(setDebouncedPhrase, 300), []);
  useEffect(() => {
    debouncedSetPhrase(phrase);
    return () => {
      debouncedSetPhrase.cancel();
    };
  }, [phrase, debouncedSetPhrase]);
  const params = {
    limit,
    phrase: debouncedPhrase,
    network,
    txnCountMin,
    liquidityMin,
    volumeMin,
    mktCapMin,
    timeFrame,
    rankingBy,
    rankingDirection,
    addNatives,
    sortSearchResultsViaAlgo,
    identifier,
    minAgeMinutes,
    filterStables,
    maxAgeMinutes,
    minUniqueTransactions,
    showPotentialScam
  };
  const query = useQuery({
    queryKey: ['fetchTokens', ...Object.values(params)],
    staleTime: cacheTime,
    queryFn: async () => {
      const results = await fetchTokens(Object.assign({}, params));
      return results;
    },
    enabled: !!identifier && enabled
  });
  return query;
}
export default useTokenSearch;